// File: webelieve-frontend/src/features/admin/components/settings/intake-forms/FormList.tsx
// EF-6.4: Form list / management view

import React, { useEffect, useState } from 'react';
import { PlusIcon, DocumentDuplicateIcon, PencilSquareIcon, TrashIcon, LinkIcon } from '@heroicons/react/24/outline';
import type { IntakeForm } from './intakeFormApi';
import { intakeFormApi } from './intakeFormApi';

interface FormListProps {
  onEdit: (formId: string) => void;
  onCreate: () => void;
}

const FormList: React.FC<FormListProps> = ({ onEdit, onCreate }) => {
  const [forms, setForms] = useState<IntakeForm[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const loadForms = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await intakeFormApi.listForms();
      setForms(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load forms');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadForms();
  }, []);

  const handleDuplicate = async (formId: string) => {
    try {
      await intakeFormApi.duplicateForm(formId);
      await loadForms();
    } catch (err: any) {
      alert(err.message || 'Failed to duplicate form');
    }
  };

  const handleDelete = async (formId: string, formName: string) => {
    if (!confirm(`Delete "${formName}"? This action cannot be undone.`)) return;
    try {
      await intakeFormApi.deleteForm(formId);
      await loadForms();
    } catch (err: any) {
      alert(err.message || 'Failed to delete form');
    }
  };

  const handleShare = (formId: string) => {
    const url = `${window.location.origin}/form/${formId}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedId(formId);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-lex-accent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-16">
        <p className="text-sm text-red-500 mb-3">{error}</p>
        <button onClick={loadForms} className="text-sm text-lex-accent hover:underline">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Intake Forms</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Build and manage client intake forms for your website
          </p>
        </div>
        <button
          onClick={onCreate}
          className="inline-flex items-center gap-1.5 px-3 py-2 bg-lex-accent text-lex-accent-contrast text-sm font-medium rounded-md hover:bg-lex-accent-hover transition-colors"
        >
          <PlusIcon className="h-4 w-4" />
          Create Form
        </button>
      </div>

      {/* Empty state */}
      {forms.length === 0 && (
        <div className="text-center py-16 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
          <div className="mx-auto h-12 w-12 rounded-full bg-lex-accent-soft dark:bg-lex-accent-soft flex items-center justify-center mb-4">
            <PlusIcon className="h-6 w-6 text-lex-accent dark:text-lex-accent" />
          </div>
          <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-1">No intake forms yet</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Create your first form to start collecting client information
          </p>
          <button
            onClick={onCreate}
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-lex-accent text-lex-accent-contrast text-sm font-medium rounded-md hover:bg-lex-accent-hover"
          >
            <PlusIcon className="h-4 w-4" />
            Create Form
          </button>
        </div>
      )}

      {/* Form cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {forms.map((form) => (
          <div
            key={form.form_id}
            className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md transition-shadow"
          >
            {/* Card header */}
            <div className="flex items-start justify-between mb-2">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white line-clamp-1">
                {form.form_name}
              </h3>
              <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                {form.is_default && (
                  <span className="text-[10px] px-1.5 py-0.5 bg-lex-accent-soft dark:bg-lex-accent-soft text-lex-accent dark:text-lex-accent rounded font-medium">
                    Default
                  </span>
                )}
                {form.is_active && (
                  <span className="text-[10px] px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded font-medium">
                    Active
                  </span>
                )}
              </div>
            </div>

            {/* Description */}
            {form.description && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 line-clamp-2">
                {form.description}
              </p>
            )}

            {/* Meta */}
            <div className="flex items-center gap-3 text-[10px] text-gray-400 dark:text-gray-500 mb-2">
              <span>{form.form_schema.fields.length} fields</span>
              <span>Created {new Date(form.created_at).toLocaleDateString()}</span>
            </div>

            {/* Public URL */}
            <div className="mb-3">
              <p className="text-[10px] text-gray-400 dark:text-gray-500 mb-0.5">Public URL</p>
              <div
                onClick={() => handleShare(form.form_id)}
                className="flex items-center gap-1.5 px-2 py-1.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded text-[11px] text-lex-accent dark:text-lex-accent cursor-pointer hover:bg-lex-state-hover dark:hover:bg-lex-state-hover transition-colors truncate"
                title="Click to copy URL"
              >
                <LinkIcon className="h-3 w-3 flex-shrink-0" />
                <span className="truncate">{window.location.origin}/form/{form.form_id}</span>
                {copiedId === form.form_id && (
                  <span className="text-green-600 dark:text-green-400 flex-shrink-0 ml-auto text-[10px] font-medium">Copied!</span>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-1 border-t border-gray-100 dark:border-gray-700 pt-3">
              <button
                onClick={() => onEdit(form.form_id)}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                title="Edit form"
              >
                <PencilSquareIcon className="h-3.5 w-3.5" />
                Edit
              </button>
              <button
                onClick={() => handleDuplicate(form.form_id)}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                title="Duplicate form"
              >
                <DocumentDuplicateIcon className="h-3.5 w-3.5" />
                Duplicate
              </button>
              <button
                onClick={() => handleShare(form.form_id)}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                title="Copy share URL"
              >
                <LinkIcon className="h-3.5 w-3.5" />
                {copiedId === form.form_id ? 'Copied!' : 'Share'}
              </button>
              <div className="flex-1" />
              <button
                onClick={() => handleDelete(form.form_id, form.form_name)}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                title="Delete form"
              >
                <TrashIcon className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default FormList;
